/**
 * ContentCachePage.tsx - Content assets, inference cache & Milvus cache management
 */

import { useCallback, useEffect, useState } from "react";
import {
  cleanupContentCache,
  getAdminContentCacheEntries,
  getAdminContentCacheStats,
  getAdminMilvusCacheEntries,
  type AdminContentCacheEntry,
  type AdminContentCacheStats,
  type AdminMilvusCacheEntry,
} from "../api/adminClient";

const inputClass =
  "px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

const selectClass = inputClass;

function fmtBytes(n: number) {
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function requestTypeLabel(t: string) {
  const map: Record<string, string> = {
    embed_image: "图片向量",
    embed_text: "文本向量",
    embed_fused: "融合向量",
    ocr: "OCR",
  };
  return map[t] || t;
}

function MetricCard({
  label,
  value,
  sub,
  index,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  index: number;
}) {
  return (
    <div
      className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)] card-hover animate-card-enter"
      style={{ "--stagger-index": index } as React.CSSProperties}
    >
      <div className="text-[13px] font-medium text-[var(--color-on-surface-variant)] mb-2">
        {label}
      </div>
      <div className="text-2xl font-semibold text-[var(--color-on-surface)] tabular-nums">
        {value}
      </div>
      {sub && (
        <div className="mt-2 text-xs text-[var(--color-on-surface-variant)]">{sub}</div>
      )}
    </div>
  );
}

const thClass = "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)]";

interface ContentCachePageProps {
  onAuthError?: () => void;
}

export default function ContentCachePage({ onAuthError }: ContentCachePageProps) {
  const [stats, setStats] = useState<AdminContentCacheStats | null>(null);
  const [entries, setEntries] = useState<AdminContentCacheEntry[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [requestType, setRequestType] = useState("");
  const [contentType, setContentType] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, listData] = await Promise.all([
        getAdminContentCacheStats(),
        getAdminContentCacheEntries({
          page,
          limit: 20,
          request_type: requestType || undefined,
          content_type: contentType || undefined,
        }),
      ]);
      setStats(statsData);
      setEntries(listData.items ?? []);
      setPages(listData.pages);
      setTotal(listData.total);
    } catch (e: unknown) {
      setStats(null);
      setEntries([]);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.includes("401") || msg.includes("403") || msg.includes("管理员")) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [page, requestType, contentType, onAuthError]);

  const handleCleanup = async () => {
    if (!window.confirm("确定清理过期缓存条目？")) return;
    setCleaning(true);
    setCleanupMessage(null);
    try {
      const result = await cleanupContentCache();
      setCleanupMessage(`已清理 ${result.deleted ?? 0} 条过期缓存`);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            数据资产
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            云端 SQLite 推理缓存，按内容哈希去重存储
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCleanup}
            disabled={cleaning || loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium border border-[var(--color-outline-variant)] text-[var(--color-on-surface)] disabled:opacity-50"
          >
            {cleaning ? "清理中..." : "清理过期缓存"}
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
          >
            {loading ? "加载中..." : "刷新"}
          </button>
        </div>
      </div>

      {cleanupMessage && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--color-success-container)] text-[var(--color-on-success-container)] text-sm">
          {cleanupMessage}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <MetricCard
          index={0}
          label="云端原始资产"
          value={stats?.total_assets.toLocaleString() ?? "—"}
          sub={
            stats
              ? `图片 ${stats.image_count} · 文本 ${stats.text_count}`
              : undefined
          }
        />
        <MetricCard
          index={1}
          label="云端存储占用"
          value={stats ? fmtBytes(stats.total_storage_bytes) : "—"}
        />
        <MetricCard
          index={2}
          label="近 30 天命中率"
          value={stats ? `${stats.cache_hit_rate_30d}%` : "—"}
          sub={
            stats
              ? `云端命中 ${stats.cache_hits_30d} / ${stats.logs_30d} 次`
              : undefined
          }
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <MetricCard index={3} label="云端图片向量" value={stats?.embed_image_count ?? "—"} />
        <MetricCard index={4} label="云端文本向量" value={stats?.embed_text_count ?? "—"} />
        <MetricCard index={5} label="云端融合向量" value={stats?.embed_fused_count ?? "—"} />
        <MetricCard index={6} label="云端 OCR" value={stats?.ocr_count ?? "—"} />
      </div>

      {/* SQLite cloud cache section */}
      <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
            云端推理缓存（SQLite）
            {total > 0 && (
              <span className="ml-2 text-sm font-normal text-[var(--color-on-surface-variant)]">
                共 {total} 条
              </span>
            )}
          </h2>
          <div className="flex flex-wrap gap-2">
            <select
              value={requestType}
              onChange={(e) => {
                setRequestType(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">全部类型</option>
              <option value="embed_image">图片向量</option>
              <option value="embed_text">文本向量</option>
              <option value="embed_fused">融合向量</option>
              <option value="ocr">OCR</option>
            </select>
            <select
              value={contentType}
              onChange={(e) => {
                setContentType(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">全部内容</option>
              <option value="image">图片</option>
              <option value="text">文本</option>
            </select>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--color-on-surface-variant)]">
            {loading ? "加载中..." : "暂无云端缓存数据"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className={thClass}>类型</th>
                  <th className={thClass}>模型</th>
                  <th className={thClass}>内容哈希</th>
                  <th className={thClass}>内容预览</th>
                  <th className={thClass}>大小</th>
                  <th className={thClass}>命中</th>
                  <th className={thClass}>创建时间</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr
                    key={row.cache_key}
                    className="border-b border-[var(--color-border-subtle)] last:border-0"
                  >
                    <td className={tdClass}>{requestTypeLabel(row.request_type)}</td>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {row.provider}/{row.model}
                      {row.dimension ? ` · ${row.dimension}d` : ""}
                    </td>
                    <td className={`${tdClass} font-mono text-xs max-w-[140px] truncate`} title={row.content_hash}>
                      {row.content_hash.slice(0, 12)}…
                    </td>
                    <td className={`${tdClass} max-w-xs text-xs`}>
                      {row.text_preview ? (
                        <span className="line-clamp-2 whitespace-pre-wrap break-all font-mono">{row.text_preview}</span>
                      ) : row.content_type === "text" ? (
                        <span className="text-[var(--color-outline)]">（无文本）</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={tdClass}>
                      {row.byte_size ? fmtBytes(row.byte_size) : row.text_length ? `${row.text_length} 字` : "—"}
                    </td>
                    <td className={`${tdClass} tabular-nums`}>{row.hit_count}</td>
                    <td className={`${tdClass} text-xs text-[var(--color-on-surface-variant)]`}>
                      {row.created_at?.replace("T", " ").slice(0, 19) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-[var(--color-on-surface-variant)] tabular-nums">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
